import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { STORAGE_KEYS } from '../utils/storageKeys';
import { Storage } from '../utils/storage';

/**
 * Validates and normalizes a client ID string.
 * Normalizes to lowercase and validates against pattern: alphanumeric, dashes, underscores, length 1-64.
 * This prevents injection of unexpected characters and ensures reasonable length.
 *
 * @param clientId - The client ID to validate and normalize
 * @returns The normalized client ID if valid, null otherwise
 */
function validateAndNormalizeClientId(
  clientId: string | null | undefined
): string | null {
  if (!clientId) return null;

  // Normalize to lowercase
  const normalized = clientId.toLowerCase();

  // Validate pattern: alphanumeric, dashes, underscores, length 1-64
  const clientIdPattern = /^[a-z0-9_-]{1,64}$/;
  if (!clientIdPattern.test(normalized)) {
    return null;
  }

  return normalized;
}

/**
 * Checks if the hostname matches the new URL structure: dashboard.{client}.{stack}.{domain}
 * This structure is used across all environments (devo, pre-prod, prod) and allows
 * clientId to be auto-extracted from the subdomain.
 *
 * The domain part can have multiple segments (e.g., org.co.in, locus-api.com).
 * Requires at least 4 parts: dashboard + client + stack + domain.
 *
 * Legacy URLs like `devo.encore.locus-dev.com` will return false, requiring manual clientId input.
 *
 * @param hostname - The hostname to check
 * @returns true if hostname matches new structure, false otherwise
 */
export function hasSubdomainClientId(
  hostname: string | null | undefined
): boolean {
  if (!hostname || hostname === 'localhost') return false;
  const parts = hostname.split('.');
  // Must have at least 4 parts: dashboard.{client}.{stack}.{domain}
  // Domain can have multiple parts (e.g., org.co.in), so we check for >= 4
  return parts.length >= 4 && parts[0] === 'dashboard';
}

/**
 * Extracts clientId from new URL structure: dashboard.{client}.{stack}.{domain}
 * Returns the client part (second subdomain) if pattern matches and validates, null otherwise.
 *
 * This only works for the new URL structure. Legacy URLs will return null.
 * The domain part can have multiple segments (e.g., org.co.in, locus-api.com).
 * Validates that clientId matches pattern: alphanumeric, dashes, underscores, 1-64 chars.
 *
 * @param hostname - The hostname to extract clientId from
 * @param hasSubdomain - Optional precomputed flag indicating if hostname has subdomain structure.
 *                       If not provided, will be computed internally.
 */
export function extractClientIdFromSubdomain(
  hostname: string | null | undefined,
  hasSubdomain?: boolean
): string | null {
  if (!hostname) return null;
  // Use provided flag if available, otherwise compute it
  const hasSubdomainFlag = hasSubdomain ?? hasSubdomainClientId(hostname);
  if (!hasSubdomainFlag) return null;
  const parts = hostname.split('.');
  // Pattern: dashboard.{client}.{stack}.{domain}
  // Extract client from parts[1]
  const extracted = parts[1];
  if (!extracted) return null;

  return validateAndNormalizeClientId(extracted);
}

function extractClientIdFromStorage(): string | null {
  try {
    const stored = Storage.get(STORAGE_KEYS.clientId);
    return stored || null;
  } catch (_e) {
    // eslint-disable-next-line no-console
    console.error('Failed to extract client id from storage:', _e);
    return null;
  }
}

export function useClientId(): string {
  const params = useParams();
  const routeClientId = (params as Record<string, string | undefined>)
    ?.clientId;

  const resolvedClientId = useMemo(() => {
    return getClientIdFromUrl(routeClientId);
  }, [routeClientId]);

  // Return empty string while logout is triggered to avoid render crashes
  return resolvedClientId || '';
}

/**
 * Extracts clientId from URL without React hooks (for use in non-React contexts).
 * Uses the same priority as useClientId: subdomain > route param > localStorage
 *
 * @param routeClientId - Optional client ID from route params (e.g., from React Router's useParams)
 * @returns The client ID if found, null otherwise
 */
export function getClientIdFromUrl(
  routeClientId?: string | undefined
): string | null {
  if (!globalThis.window?.location) return null;

  const hostname = globalThis.window.location.hostname;
  const hasSubdomain = hasSubdomainClientId(hostname);

  // Priority 1: subdomain (source of truth for new URL structure)
  const fromSubdomain = extractClientIdFromSubdomain(hostname, hasSubdomain);
  if (fromSubdomain) {
    return fromSubdomain;
  }

  // Priority 2: route param (from React Router or parsed from URL path)
  // Validate routeClientId using the same pattern as extractClientIdFromSubdomain
  if (routeClientId && !hasSubdomain) {
    // For legacy URLs, allow route param from React Router
    const normalized = validateAndNormalizeClientId(routeClientId);
    if (normalized) {
      return normalized;
    }
    // If validation fails, fall through to next priority
  }

  // If no route param provided, try parsing from URL path
  const pathRegex = /\/clients\/([^/]+)/;
  const pathMatch = pathRegex.exec(globalThis.window.location.pathname);
  if (pathMatch?.[1] && !hasSubdomain) {
    const normalized = validateAndNormalizeClientId(pathMatch[1]);
    if (normalized) {
      return normalized;
    }
    // If validation fails, fall through to next priority
  }

  // Priority 3: localStorage (used for legacy URLs, manual entry on login screen)
  if (!hasSubdomain) {
    return extractClientIdFromStorage();
  }

  return null;
}
